import Image from 'next/image'

import styles from './styles.module.scss'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faMessage, faPaperPlane, faXmark } from '@fortawesome/free-solid-svg-icons'

import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

import AuctionListing from '../AuctionListing/AuctionListing'
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner'

import { useSocket } from '../../../Context/socket'
import { useSession } from 'next-auth/react'
import axios from 'axios'
import { BarLoader, PacmanLoader, PuffLoader, PulseLoader } from 'react-spinners'
import errorMsg from '../../../Functions/errorMsg'
import successMsg from '../../../Functions/successMsg'
import { isEqual } from 'lodash'

import NumberFormat from 'react-number-format';
import { useRouter } from 'next/router'

export default function Bids({ setPendingRequest }) {

  const { socket } = useSocket();
  const { data: session, status } = useSession();

  const [initialLoading, setInitialLoading] = useState(null);
  const [biddingListings, setBiddingListings] = useState(null);

  useEffect(() => {
    if(initialLoading !== null) return;
    if(biddingListings !== null) return;
    if(status === 'loading') return;
    if(!session) return;

    async function getMyListings() {
        setInitialLoading(true);
        setPendingRequest(true);

        const result = await axios.post(`${window.location.origin}/api/auctionHub/biddedAuctions`, {
          getRequestingID: session.user.token.id
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString())
            setPendingRequest(false);
        })

        if(result && result.status === 200) {
          const data = result.data;
          setBiddingListings(data);
          setPendingRequest(false);
        }

    }

    getMyListings();

  }, [session, status])

  function isLoading() {
    if(status === 'loading') return true;
    if(initialLoading === false || initialLoading === null) return true;
    if(biddingListings === null) return true;
    return false;
  }

  const [viewListingData, setViewListingData] = useState(null); // ListingID Stored

  return (
    <section id={styles.wrapper}>
      { viewListingData !== null && <AuctionListing socket={socket} listingID={viewListingData} closeListing={setViewListingData} /> }

      { isLoading() === true && <section id={styles.loading}><PulseLoader size={30} color='#154734' /></section> }

      { isLoading() === false && (biddingListings.length === 0 ?
        <div id={styles.noListings}>
          <h1>Your watchlist is empty</h1>
        </div>
        :
        <section id={styles.container}>
          {
            biddingListings.map((listing, index) => {
              return <ListingCard key={index} listing={listing} setViewListingData={setViewListingData} />
            })
          }
        </section>
      )}

  </section>
  )
}

const ListingCard = ({ listing, setViewListingData }) => {

  const router = useRouter();
  const { data: session, status } = useSession();
  const { socket, auctionSocketConnected, auctionSocket } = useSocket();

  const listingID = listing._id;

  const [coverLoaded, setCoverLoaded] = useState(false);
  const getCover = listing.images.findIndex((images) => images.position === 1);

  const [bids, setBids] = useState(listing.bids.length > 0 ? listing.bids.sort((a, b) => b.bid - a.bid) : []);

  const [loadingBids, setLoadingBids] = useState(false);

  useEffect(() => {
    async function setupBidCAD() {
      if (!listingID) {
        return errorMsg("There was an error loading the bidding data for this auction, please try again later.");
      }

      setLoadingBids(true);

      const getAuctionBids = await axios.post(`${window.location.origin}/api/auctionHub/helpers/getAuctionBids`, {
        auctionID: listingID,
      }).catch((error) => {
        errorMsg(error?.response?.data?.status || error.toString());
      });

      if (getAuctionBids.status === 200) {
        const result = getAuctionBids.data;
        setBids(result);
      } else {
        setBids(false);
      }

      setLoadingBids(false);
    }
  
    setupBidCAD();

  }, [listingID]);

  function handleIncomingBid(data) {
    const incomingAuctionID = data.auctionID;
    if(incomingAuctionID !== listingID) return;
    const username = data.username;
    const bid = data.bid;

    const newBid = { incomingAuctionID, username, bid };
    const updatedBids = [...bids, newBid];

    updatedBids.sort((a, b) => b.bid - a.bid);

    setBids(updatedBids);
  }

  function getHighestBid() {
    if(bids.length === 0) return { bid: 0.00 }
    const sortedBidsArray = [...bids].sort((a, b) => b.bid - a.bid);
    if(!isEqual(sortedBidsArray, bids)) {
      setBids(sortedBidsArray);
    }

    return sortedBidsArray[0]
  }

  async function getFinalBidArray() {
    if(loadingBids) return;

    setLoadingBids(true);

    const getAuctionBids = await axios.post(`${window.location.origin}/api/auctionHub/helpers/getAuctionBids`, {
      auctionID: listingID,
    }).catch((error) => {
      errorMsg(error?.response?.data?.status || error.toString());
    });

    if (getAuctionBids.status === 200) {
      const result = getAuctionBids.data;
      setBids(result);
    }

    setLoadingBids(false);
  }

  const [auctionState, setAuctionState] = useState(null);

  useEffect(() => {
    if(!auctionSocket || !auctionSocketConnected) return;
    if(auctionState !== 'Counting Down') return;

      const auctionRoomID = listingID;

      auctionSocket.emit('join-auction-room', { roomID: auctionRoomID });

    return () => {
      auctionSocket.emit('leave-auction-room', { roomID: auctionRoomID });
    };
  }, [auctionSocket, auctionState]);

  useEffect(() => {
    if(!auctionSocket || !auctionSocketConnected) return;
    if(auctionState !== 'Counting Down') return;

    auctionSocket.on('incomingBid', handleIncomingBid);

    return () => {
      auctionSocket.off('incomingBid');
    };
  }, [auctionSocket, auctionState])


  const [userBid, setUserBid] = useState('');
  const [pendingBid, setPendingBid] = useState(false);

  async function submitBid(e) {
    e.preventDefault();
    if(!userAuthenticated()) return router.push(`/account/login`);
    if(pendingBid === true) return;
    if(userBid.length === 0) return errorMsg("Please enter your bid before submitting.");

    const submittedBid = userBid;

    const bidDataRaw = {
      auctionID: listingID,
      bidID: null,
      userID: session.user.token.id,
      username: session.user.token.username,
      bid: submittedBid,
      created: new Date().toISOString()
    }

    setPendingBid(true);

    const submitBidResult = await axios.post(`${window.location.origin}/api/auctionHub/helpers/submitBid`, bidDataRaw).catch((error) => {
      errorMsg(error?.response?.data?.status || error.toString())
    });

    if(submitBidResult && submitBidResult.status === 200) {
      const verifiedBidData = submitBidResult.data;
      auctionSocket.emit('submitBid', { auctionID: verifiedBidData.auctionID, username: verifiedBidData.username, bid: verifiedBidData.bid })
      setUserBid('');
      successMsg("Your bid was submitted.");
    }

    setPendingBid(false);

  }

  const priceValueCap = (inputObj) => {
    const { value } = inputObj;
    if (value <= 100000000) return true;
    return false;
  };

  function userAuthenticated() {
    if(!session) return false;
    if(status !== 'authenticated') return false;
    if(!session.user.token.id) return false;
    return true;
  }

  const [pendingHandleMessageOperation, setPendingHandleMessageOperation] = useState(false);
  const [openNewMessage, setOpenNewMessage] = useState(null);

  async function handleMessageSellerOperation() {
    if(pendingHandleMessageOperation) return;
    if(!session) return;
    if(status !== 'authenticated') return;
    if(bids.length === 0) return;
    if(!getHighestBid().username) return;

    setPendingHandleMessageOperation(true);

    const result = await axios.post(`${window.location.origin}/api/messaging/hasActiveConvo`, {
        requestingUserID: session.user.token.id,
        otherUser: listing.owner,
        convoType: 'Auctions',
        listingID: listingID,
    }).catch((error) => {
        errorMsg(error?.response?.data?.status || error.toString());
        closeProfile();
    })

    if(result && result.status === 200) {
        const data = result.data;

        if(data.hasConvo) {
          handleOpenCurrentConversation(data.convoID);
        } else {
          setOpenNewMessage(true);
        }

    }

    setPendingHandleMessageOperation(false);

  }

  function handleOpenCurrentConversation(convoID) {
    const { query } = router;

    const updatedQuery = { ...query, openConversationID: convoID };

    router.push({ pathname: router.pathname, query: updatedQuery });
  }

  const [pendingSend, setPendingSend] = useState(false);
  const messageRef = useRef(null);

  async function handleSendMessage(e) {
    e.preventDefault();
    if(status === 'loading') return;
    if(!socket) return;
    if(!session) return errorMsg("Please login to message this user.");
    if(status === 'unauthenticated') return errorMsg("Please login to message this user.");
    if(pendingSend === true) return;
    if(!messageRef || !messageRef.current) return;
    const messageText = messageRef.current.value;
    if(messageText.length === 0) return errorMsg("Please enter your message before sending.");
    if(messageText.length > 500) return errorMsg("Message cannot be over 500 characters.")
    
    const senderID = session.user.token.id;
    const sender = session.user.token.username;
    const toUser = getHighestBid().username;
    if(!toUser) return errorMsg("Could not find the buyer, please contact us if the issue persists.");

    const basicConvoData = {
        type: 'Auctions',
        listingID: listingID,
        receiver: toUser,
        sender: sender,
        creationDate: new Date().toISOString()
    }

    setPendingSend(true);

    const result = await axios.post(`${window.location.origin}/api/messaging/create`, {
        conversationData: basicConvoData,
        messageText: messageText,
        senderID: senderID
    }).catch((error) => {
        errorMsg(error?.response?.data?.status || error.toString())
    });

    if(result && result.status === 200) {
        const newConvoData = result.data;

        socket.emit('createNewConversation', newConvoData)

        successMsg("Your message was sent.");

        setOpenNewMessage(false);
    }

    setPendingSend(false);
  }

  return (
    <div className={styles.card}>

      <div style={{display: openNewMessage ? 'flex' : 'none'}} id={styles.quickMessage}>
        <FontAwesomeIcon onClick={() => setOpenNewMessage(false)} icon={faXmark} id={styles.exit} />
        <h1>Chat with {getHighestBid().username}</h1>
        <textarea ref={messageRef} placeholder="Write this user a message..." />
        <button onClick={handleSendMessage}> {pendingSend ? <BarLoader color='#4169E1' /> : <FontAwesomeIcon icon={faPaperPlane} /> } </button>
      </div>

     <section>
        <TimeStampContainer duration={listing.auctionDuration} created={listing.creationDate} handleGetFinalBidsArray={getFinalBidArray} auctionState={auctionState} setAuctionState={setAuctionState} />
        <div style={{display: coverLoaded === false ? 'flex' : 'none'}}> <LoadingSpinner type="pulse" color="#154734" /></div>
        <Image sizes='auto auto' fill src={listing.images[getCover].url} alt={listing.images[getCover].url} onLoadingComplete={() => setCoverLoaded(true)} />
      </section>

      { (loadingBids || !auctionState) ?
        <section style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <BarLoader color='black' />
        </section>
        :
        <section>
          <div>
            <h1>{listing.title}</h1>
            { auctionState === 'Auction Closed' && <h2>{bids.length === 0 ? <b>No Winner</b> : <> <b>{getHighestBid().username === session.user.token.username ? 'You' : getHighestBid().username}</b> won with bid ${parseFloat(getHighestBid().bid).toLocaleString()} </> }</h2> }
            { auctionState !== 'Auction Closed' && <h2><b>{bids.length === 0 ? 'Bid' : 'Bid'}</b>: ${bids.length === 0 ? listing.price : parseFloat(getHighestBid().bid.toLocaleString()).toLocaleString()} </h2> }
            
            { listing.owner !== session?.user?.token?.username &&
              <div style={{display: auctionState === 'Counting Down' ? 'flex' : 'none'}}>
                <NumberFormat onValueChange={(values, sourceInfo) => { !pendingBid && setUserBid(values.value)}} isAllowed={priceValueCap} decimalSeparator={false} placeholder="Max Bid" thousandSeparator={true} prefix={'$'} value={userBid} />
                <button disabled={pendingBid} onClick={submitBid}>
                  { pendingBid ? <PacmanLoader size={10} color='white' /> : 'Quick Bid' }
                </button>
              </div>
            }

          </div>

          <div>
            <motion.button onClick={() => setViewListingData(listingID)}><FontAwesomeIcon icon={faEye} id={styles.icon} /></motion.button>
            <motion.button onClick={handleMessageSellerOperation}> { pendingHandleMessageOperation ? <PuffLoader size={22} color='white' /> : <FontAwesomeIcon icon={faMessage} id={styles.icon} /> }  </motion.button>
          </div>

        </section>
      }
    </div>
  )
}

const TimeStampContainer = ({ duration, created, handleGetFinalBidsArray, auctionState, setAuctionState }) => {
  const [timeStamps, setTimeStamps] = useState(null);

  useEffect(() => {
    if(duration === null || duration === undefined || created === null || created === undefined) return;

    const countdownInterval = setInterval(() => {

      const timestamp = getTimeStamp(duration, created);

      if(timestamp === 'Auction Closed' || timestamp === 'Calculating winner...') {
        if(auctionState !== timestamp) {
          setAuctionState(timestamp);
        }
      } else {
        if(auctionState !== 'Counting Down') {
          setAuctionState("Counting Down")
        }
      }

      if(timestamp === 'Auction Closed') {
        closeCountdown();
        handleGetFinalBidsArray();
        setTimeStamps("Auction Closed");
        return;
      }

      setTimeStamps(timestamp);
    }, 100);

    function closeCountdown() {
      clearInterval(countdownInterval);
    }

    return () => {
      clearInterval(countdownInterval)
    }

  }, [])

  return timeStamps !== null && <h1>{timeStamps}</h1>;
}

function getTimeStamp(duration, created) {

  const durDays = parseInt(duration);

  const ending_date = new Date(Date.parse(created));
  ending_date.setDate(ending_date.getDate() + durDays);

  const current_day = new Date();

  const total = ending_date - Date.parse(current_day); // Future ending date is subtracting by current date to get proper values
  
  const seconds = Math.floor( (total/1000) % 60 );
  const minutes = Math.floor( (total/1000/60) % 60 );
  const hours = Math.floor( (total/(1000*60*60)) % 24 );
  const days = Math.floor( total/(1000*60*60*24) );

  var returnSeconds;
  var returnMinutes;
  var returnHours;
  var returnDays;

  if(seconds < 0) {
    returnSeconds = 0;
  } else {
    returnSeconds = seconds;
  }

  if(minutes < 0) {
    returnMinutes = 0;
  } else {
    returnMinutes = minutes;
  }

  if(hours < 0) {
    returnHours = 0;
  } else {
    returnHours = hours;
  }

  if(days < 0) {
    returnDays = 0;
  } else {
    returnDays = days;
  }

  var state = null;

  if(seconds <= 0 && total >= -15000) {
    state = "Calculating winner..."
  }

  if(total < -15000) {
    state = "Auction Closed"
  }

  if(total > 0) {
    state = `${returnDays}d : ${returnHours}hr : ${returnMinutes}m : ${returnSeconds}s`
  }

  return state
}

const NewFunc = ({}) => {
  return (
    <div>
<h1>Review & Sign</h1>
      <p>In making application for employment:</p>
      <ul style="margin-left: 16px; margin-top: 12px">
        <li>I certify that the information in this application is true and complete for all practical purposes. It may be verified by the Agency or any affiliate. Should a position be offered and later it is found that the information is significantly untrue, incomplete, or misrepresented, I understand and agree that the Agency or its affiliates are relieved of all commitments, financial or otherwise pertinent to employment, and that I am subject to immediate termination without recourse.</li>
        <li>I understand and agree that if I am offered employment by the Agency, my employment will be for no definite term and that either I, or the Agency will have the right to terminate the employment relationship at any time, with or without cause, and with or without notice. I also understand that this status can only be altered by a written contract of employment which is specific as to all material terms and is signed by me and the Administrator of the Agency.</li>
      <li>I understand that if I have direct patient/client contact the Agency will perform a background check, including criminal history check (if applicable), OIG LEIE check (if applicable), and any additional checks as required by accrediting body standards or state regulations. I further understand, if I am an unlicensed person, the Agency will perform a check of the Nurse Aide Registry and Employee Misconduct Registry. I understand that: 1) the purpose of the Employee Misconduct Registry is to ensure that unlicensed personnel who commit acts of abuse, neglect, exploitation, misappropriation, or misconduct against residents and consumers are denied employment in HHS-regulated facilities and agencies; 2) the State of Texas maintains a registry of all nurse aides who are certified to provide services in nursing facilities and skilled nursing facilities licensed by the Texas Health and Human Services (HHS) and they review and investigate allegations of abuse, neglect, or misappropriation of resident property by nurse aides and if there's a finding of an alleged act of abuse, neglect, or misappropriation, the nurse aide may request both an informal reconsideration and a formal hearing before the finding is placed on the registry; 3) All HHS-regulated facilities and agencies are required to check the Employee Misconduct Registry and Nurse Aide Registry before hire to determine if I am listed in either registry as having committed an act of abuse, neglect, exploitation, misappropriation, or misconduct against a resident or consumer and am, therefore, unemployable. I understand that a refusal to authorize the criminal background check may result in adverse employment action, such as rejection of the application or termination of employment.</li>
      <li><b>RELEASE: </b>I hereby authorize any prior employers to provide such information concerning my employment with them as may be requested, and also authorize the Registrar / enrollment or admissions office of all educational institutions attended to release an official copy of my transcript and, if available, faculty appraisals. I also authorize any appropriate licensing board to release full information concerning my license status and my license history.</li>
      </ul>
      <h3 style="margin-top: 25px">Statement of Employability</h3>
      <p>By execution of this document, I acknowledge that I have been informed by Bennington Home Health Care, LLC and agree that Bennington Home Health Care, LLC may conduct a State of Texas criminal history check. I agree to a search of the Nurse Aid Registry and the Employee Misconduct Registry prior to employment and at least every 12 months if hired. I understand that these checks will determine if I have a criminal conviction or have committed certain conduct that will bar me from employment with Bennington Home Health Care, LLC. I understand that I am unemployable if listed in the NAR or EMR per TAC 97.247, THSC 250, and THSC 250.006.</p>
      <h3 style="margin-top: 25px">Criminal History Check</h3>
      <p>I have informed Bennington Home Health Care, LLC of all names (i.e. maiden, aliases) that I have used in the past. I understand that my employment is pending the result of the criminal history check, and that I may not have face-to-face client contact until results are returned. I will be notified of results.</p>
      <h2>Convictions Barring Employment</h2>
      <h2>Bars Pursuant to Health and Safety Code §250.006</h2>
      <h2>Texas Penal Code (Permanent Bar)</h2>
      <ul style="margin-left: 16px; margin-bottom: 25px; margin-top: 25px;">
      <li>an offense under Chapter 19, Penal Code (criminal homicide);</li>
      <li>an offense under Chapter 20, Penal Code (kidnapping, unlawful restraint, and smuggling of persons);</li>
      <li>an offense under Section 21.02, Penal Code (continuous sexual abuse of young child or children), or Section 21.11, Penal Code (indecency with a child);</li>
      <li>an offense under Section 22.011, Penal Code (sexual assault);</li>
      <li>an offense under Section 22.02, Penal Code (aggravated assault);</li>
      <li>an offense under Section 22.04, Penal Code (injury to a child, elderly individual, or disabled individual);</li>
      <li>an offense under Section 22.041, Penal Code (abandoning or endangering child);</li>
      <li>an offense under Section 22.08, Penal Code (aiding suicide);</li>
      <li>an offense under Section 25.031, Penal Code (agreement to abduct from custody);</li>
      <li>an offense under Section 25.08, Penal Code (sale or purchase of child);</li>
      <li>an offense under Section 28.02, Penal Code (arson);</li>
      <li>an offense under Section 29.02, Penal Code (robbery);</li>
      <li>an offense under Section 29.03, Penal Code (aggravated robbery);</li>
      <li>an offense under Section 21.08, Penal Code (indecent exposure);</li>
      <li>an offense under Section 21.12, Penal Code (improper relationship between educator and student);</li>
      <li>an offense under Section 21.15, Penal Code (invasive visual recording);</li>
      <li>an offense under Section 22.05, Penal Code (deadly conduct);</li>
      <li>an offense under Section 22.021, Penal Code (aggravated sexual assault);</li>
      <li>an offense under Section 22.07, Penal Code (terroristic threat);</li>
      <li>an offense under Section 32.53, Penal Code (exploitation of child, elderly individual, or disabled individual);</li>
      <li>an offense under Section 33.021, Penal Code (online solicitation of a minor);</li>
      <li>an offense under Section 34.02, Penal Code (money laundering);</li>
      <li>an offense under Section 35A.02, Penal Code (health care fraud);</li>
      <li>an offense under Section 36.06, Penal Code (obstruction or retaliation);</li>
      <li>an offense under Section 42.09, Penal Code (cruelty to livestock animals), or under Section 42.092, Penal Code (cruelty to non-livestock animals); or</li>
      <li>a conviction under the laws of another state, federal law, or the Uniform Code of Military Justice for an offense containing elements that are substantially similar to the elements of an offense listed by this subsection.</li>
      </ul>
      <h2 style="margin-top: 25px;">Texas Penal Code (5-year Bar)</h2>
      <ul style="margin-left: 16px; margin-bottom: 25px">
      <li>an offense under Section 22.01, Penal Code (assault), that is punishable as a Class A misdemeanor or as a felony;</li>
      <li>an offense under Section 30.02, Penal Code (burglary);</li>
      <li>an offense under Chapter 31, Penal Code (theft), that is punishable as a felony;</li>
      <li>an offense under Section 32.45, Penal Code (misapplication of fiduciary property or property of financial institution), that is punishable as a Class A misdemeanor or a felony;</li>
      <li>an offense under Section 32.46, Penal Code (securing execution of document by deception), that is punishable as a Class A misdemeanor or a felony;</li>
      <li>an offense under Section 37.12, Penal Code (false identification as peace officer; misrepresentation of property); or</li>
      <li>an offense under Section 42.01(a)(7), (8), or (9), Penal Code (disorderly conduct); </li>
      <li>a conviction under the laws of another state, federal law, or the Uniform Code of Military Justice for an offense containing elements that are substantially similar to the elements of an offense listed by this subsection. 242 or 247 is entitled to obtain criminal history record information may not be employed in a facility licensed under Chapter 242 or 247 if the person has been convicted</li>
      <li>of an offense under Section 30.02, Penal Code (burglary); or</li>
      <li>under the laws of another state, federal law, or the Uniform Code of Military Justice for an offense containing elements that are substantially similar to the elements of an offense under Section 30.02, Penal Code.</li>
      </ul>
      <h2 style="margin-top: 25px;">Additional Bars to Employment</h2>
      <h2 style="margin-bottom: 25px;">Bars pursuant to Texas Administrative Code, Title 40, Part 1, Chapter 3, §3.201 Texas Health and Safety Code</h2>
      <p>Chapter 481 — Texas Controlled Substances Act: A conviction that is punishable as a felony (involving manufacture, delivery, intent to distribute, conspiracy to possess or produce with intent to distribute, distribution to a minor, illegal expenditure or investment, or transfer or receipt of chemical laboratory apparatus).</p>
      <h2 style="margin-top: 25px">Texas Penal Code</h2>
      <ul style="margin-left: 16px; margin-bottom: 25px">
      <li>§15.01 — Criminal Attempt of any offense listed as a bar</li>
      <li>§43.03 — Promotion of Prostitution</li>
      <li>§43.04 — Aggravated Promotion of Prostitution</li>
      <li>§43.05 — Compelling Prostitution</li>
      <li>§43.25 — Sexual Performance by a Child</li>
      <li>§43.26 — Possession or Promotion of Child Pornography</li>
      </ul>
      <p>I acknowledge that if I am found to have been convicted of any other offense(s) that these offenses may also bar my employment. I understand that all information obtained by Bennington Home Health Care, LLC regarding any criminal history will remain confidential. I certify that the information on this form contains no willful misrepresentation and that the information that is given is true and complete to the best of my knowledge.</p>
      <h3 style="margin-top: 25px">DPS Computerized Criminal History (CCH) Verification</h3>
      <p>I, the applicant and signer of this application, acknowledge that a Computerized Criminal History (CCH) check may be performed by accessing the Texas Department of Public Safety Secure Website and may be based on name and DOB identifiers. (This is not a consent form, but serves as information for the applicant.) Authority for this agency to access an individual’s criminal history data may be found in Texas Government Code 411; Subchapter F</p>
      <p>Name-based information is not an exact search and only fingerprint record searches represent true identification to criminal history record information (CHRI), therefore the organization conducting the criminal history check is not allowed to discuss with me any CHRI obtained using the name and DOB method. The agency may request that I also have a fingerprint search performed to clear any misidentification based on the result of the name and DOB search.</p>
      <p>In order to complete the fingerprint process I must make an appointment with the Fingerprint Applicant Services of Texas (FAST) as instructed online at <a href="www.txdps.state.tx.us">www.txdps.state.tx.us</a> /Crime Records/Review of Personal Criminal History or by calling the DPS Program Vendor at 1-888-467-2080, submit a full and complete set of fingerprints, request a copy be sent to the agency listed below, and pay a fee of $25.00 to the fingerprinting services company.</p>
      <p>Once this process is completed the information on my fingerprint criminal history record may be discussed with me.</p>
      <hr style="width: 100%; height: 1px; border: none; background-color: #1C7188; margin-top: 25px; margin-bottom: 25px;"></hr>
      <h2>I confirm, consent, and acknowledge the above details listed under Review & Sign by signing and dating below</h2>
    </div>
  )
}